import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import InsightsIcon from "@mui/icons-material/Insights";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  ANNOUNCEMENT_ROLES,
  type Announcement,
  type AnnouncementRole,
  type AnnouncementSeverity,
  createAnnouncement,
  deleteAnnouncement,
  getAnnouncementStats,
  listAllAnnouncements,
  listAnnouncementReads,
  patchAnnouncement,
} from "@/api/announcements";
import { PageHeader } from "@/components/PageHeader";

const SEVERITY_COLOR: Record<AnnouncementSeverity, "info" | "warning" | "error"> = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "error",
};

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export function AnnouncementsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [statsTarget, setStatsTarget] = useState<Announcement | null>(null);
  const [editTarget, setEditTarget] = useState<Announcement | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<AnnouncementSeverity>("INFO");
  const [dismissible, setDismissible] = useState(true);
  const [startsAt, setStartsAt] = useState(toLocalInput(new Date().toISOString()));
  const [endsAt, setEndsAt] = useState("");
  const [targetRoles, setTargetRoles] = useState<AnnouncementRole[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["announcements", "all"],
    queryFn: listAllAnnouncements,
  });

  const resetForm = () => {
    setTitle("");
    setBody("");
    setSeverity("INFO");
    setDismissible(true);
    setStartsAt(toLocalInput(new Date().toISOString()));
    setEndsAt("");
    setTargetRoles([]);
    setEditTarget(null);
    setFormError(null);
  };

  const startEdit = (a: Announcement) => {
    setEditTarget(a);
    setTitle(a.title);
    setBody(a.body);
    setSeverity(a.severity);
    setDismissible(a.dismissible);
    setStartsAt(toLocalInput(a.starts_at));
    setEndsAt(toLocalInput(a.ends_at));
    setTargetRoles(a.target_roles ?? []);
    setFormError(null);
    setOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        body,
        severity,
        dismissible,
        starts_at: fromLocalInput(startsAt),
        ends_at: endsAt ? fromLocalInput(endsAt) : null,
        target_roles: targetRoles.length > 0 ? targetRoles : null,
      };
      if (editTarget) {
        return patchAnnouncement(editTarget.id, payload);
      }
      return createAnnouncement(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["announcements-active"] });
      setOpen(false);
      resetForm();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["announcements"] });
      qc.invalidateQueries({ queryKey: ["announcements-active"] });
    },
  });

  return (
    <Box>
      <PageHeader
        helpTerm="annonce"
        title={t("announcements.adminTitle")}
        subtitle={t("announcements.adminSubtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            {t("announcements.create")}
          </Button>
        }
      />

      {list.isLoading && <CircularProgress />}
      {list.data && list.data.length === 0 && (
        <Alert severity="info">{t("announcements.empty")}</Alert>
      )}

      {list.data && list.data.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("announcements.severity")}</TableCell>
                <TableCell>{t("announcements.title")}</TableCell>
                <TableCell>{t("announcements.targetRoles")}</TableCell>
                <TableCell>{t("announcements.startsAt")}</TableCell>
                <TableCell>{t("announcements.endsAt")}</TableCell>
                <TableCell>{t("announcements.dismissible")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.data.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`announcements.sev.${a.severity}`)}
                      color={SEVERITY_COLOR[a.severity]}
                    />
                  </TableCell>
                  <TableCell>{a.title}</TableCell>
                  <TableCell>
                    {a.target_roles && a.target_roles.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {a.target_roles.map((r) => (
                          <Chip
                            key={r}
                            size="small"
                            variant="outlined"
                            label={t(`announcements.role.${r}`)}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="default"
                        label={t("announcements.everyone")}
                      />
                    )}
                  </TableCell>
                  <TableCell>{new Date(a.starts_at).toLocaleString()}</TableCell>
                  <TableCell>
                    {a.ends_at ? new Date(a.ends_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell>
                    {a.dismissible ? t("common.yes") : t("common.no")}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t("announcements.statsTooltip")}>
                        <IconButton size="small" onClick={() => setStatsTarget(a)}>
                          <InsightsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common.edit") ?? "Modifier"}>
                        <IconButton size="small" onClick={() => startEdit(a)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("common.delete") ?? "Supprimer"}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (confirm(t("announcements.deleteConfirm"))) {
                              deleteMutation.mutate(a.id);
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editTarget ? t("announcements.editTitle") : t("announcements.createTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t("announcements.title")}
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              inputProps={{ maxLength: 120 }}
              autoFocus
            />
            <TextField
              label={t("announcements.body")}
              size="small"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={3}
              maxRows={10}
              inputProps={{ maxLength: 2000 }}
            />
            <TextField
              label={t("announcements.severity")}
              size="small"
              select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as AnnouncementSeverity)}
            >
              <MenuItem value="INFO">{t("announcements.sev.INFO")}</MenuItem>
              <MenuItem value="WARNING">{t("announcements.sev.WARNING")}</MenuItem>
              <MenuItem value="CRITICAL">{t("announcements.sev.CRITICAL")}</MenuItem>
            </TextField>
            <TextField
              label={t("announcements.startsAt")}
              size="small"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("announcements.endsAtOpt")}
              size="small"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={t("announcements.endsAtHelp")}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                {t("announcements.targetRolesLabel")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("announcements.targetRolesHelp")}
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
                {ANNOUNCEMENT_ROLES.map((r) => {
                  const selected = targetRoles.includes(r);
                  return (
                    <Chip
                      key={r}
                      size="small"
                      label={t(`announcements.role.${r}`)}
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      onClick={() =>
                        setTargetRoles((prev) =>
                          prev.includes(r)
                            ? prev.filter((x) => x !== r)
                            : [...prev, r],
                        )
                      }
                    />
                  );
                })}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={dismissible}
                onChange={(e) => setDismissible(e.target.checked)}
              />
              <span>{t("announcements.dismissible")}</span>
            </Stack>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !title || !body || !startsAt}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <AnnouncementStatsDialog
        target={statsTarget}
        onClose={() => setStatsTarget(null)}
      />
    </Box>
  );
}


function AnnouncementStatsDialog({
  target,
  onClose,
}: {
  target: Announcement | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const open = target !== null;
  const id = target?.id;

  const stats = useQuery({
    queryKey: ["announcement-stats", id],
    queryFn: () => getAnnouncementStats(id!),
    enabled: open,
  });
  const reads = useQuery({
    queryKey: ["announcement-reads", id],
    queryFn: () => listAnnouncementReads(id!),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("announcements.statsTitle")}</DialogTitle>
      <DialogContent>
        {stats.isLoading && <CircularProgress />}
        {stats.data && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                color="success"
                label={t("announcements.seenCount", { count: stats.data.seen })}
              />
              <Chip
                color="default"
                label={t("announcements.notSeenCount", {
                  count: stats.data.not_seen,
                })}
              />
              <Chip
                variant="outlined"
                label={t("announcements.totalUsers", {
                  count: stats.data.total_users,
                })}
              />
            </Stack>
            {reads.data && reads.data.length > 0 && (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("announcements.readUser")}</TableCell>
                      <TableCell>{t("announcements.readEmail")}</TableCell>
                      <TableCell>{t("announcements.readAt")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reads.data.map((r) => (
                      <TableRow key={r.user_id}>
                        <TableCell>{r.full_name ?? "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {r.email}
                        </TableCell>
                        <TableCell>
                          {new Date(r.read_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {reads.data && reads.data.length === 0 && (
              <Alert severity="info">{t("announcements.noReadsYet")}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
